import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getUsersAsync } from "../../../slices/user";
import { createHseEvent, updateHseEvent } from "../../../api/hse/events";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { PMField } from "../../utils/PMField";
import PMCombobox from "../../utils/PMCombobox";
import { CustomerTag } from "../../utils/moduleTags/customerTag";
import { EmployeeTag } from "../../utils/moduleTags/employeeTag";
import { ProjectTag } from "../../utils/moduleTags/projectTag";
import { ProjectFolderTag } from "../../utils/moduleTags/projectFolderTag";
import { DocumentUploadSectionV2 } from "../Media/DocumentUploadSectionV2";
import { TinyDocumentSection } from "../documents/tinyDocumentSection";
import { DepartmentSelector } from "../../utils/moduleSelectors/departmentSelector";
import { MultiEmployeeSelector } from "../../utils/moduleSelectors/multiEmployeeSelector";
import { DocumentUploadSectionV5 } from "../Media/DocumentUploadSectionV5";

export const HseEventForm = ({ event, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const itemState = useSelector((store) => store.users);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showMultiEmployeeSelector, setShowMultiEmployeeSelector] =
    useState(false);
  const [involvedEmployees, setInvolvedEmployees] = useState([]);

  // Helper function to format time for HTML time input
  const formatTimeForInput = (timeValue) => {
    if (!timeValue) return "";

    // Backend now returns HH:MM format directly
    if (typeof timeValue === "string" && timeValue.match(/^\d{1,2}:\d{2}$/)) {
      return timeValue;
    }

    // Fallback for any other format
    try {
      const time = new Date(timeValue);
      if (isNaN(time.getTime())) return "";

      const hours = time.getHours().toString().padStart(2, "0");
      const minutes = time.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formatting time:", error);
      return "";
    }
  };

  // Initialize involvedEmployees from event when component mounts
  useEffect(() => {
    if (event?.involved_employees) {
      // Handle both array of user objects and string format
      if (Array.isArray(event.involved_employees)) {
        setInvolvedEmployees(event.involved_employees);
      } else if (typeof event.involved_employees === "string") {
        // If it's a string, try to parse it (fallback)
        try {
          const userIds = event.involved_employees
            .split(", ")
            .map((id) => parseInt(id.trim()));
          const users =
            itemState.data?.filter((user) => userIds.includes(user.id)) || [];
          setInvolvedEmployees(users);
        } catch (error) {
          console.error("Error parsing involved_employees:", error);
          setInvolvedEmployees([]);
        }
      }
    }
  }, [event?.involved_employees, itemState.data]);

  // Fetch users when component mounts
  useEffect(() => {
    if (!itemState.data || itemState.data.length === 0) {
      dispatch(
        getUsersAsync({
          filters: [{ name: "EMPLOYEE", value: true }],
        })
      );
    }
  }, []); // Empty dependency array - only run once on mount

  // Event types
  const eventTypes = [
    { value: "injury", title: t("Skade") },
    { value: "near_miss", title: t("Nærhendelse") },
    { value: "property_damage", title: t("Materiellskade") },
    { value: "environmental", title: t("Miljøhendelse") },
    { value: "other", title: t("Annet") },
  ];

  // Severity levels
  const severityLevels = [
    { value: "low", title: t("Lav") },
    { value: "medium", title: t("Medium") },
    { value: "high", title: t("Høy") },
    { value: "critical", title: t("Kritisk") },
  ];

  // Event categories
  const eventCategories = [
    { value: "law_violation", title: t("Avvik lov") },
    { value: "regulation_violation", title: t("Avvik forskrift") },
    { value: "procedure_violation", title: t("Brudd på prosedyre") },
    { value: "training_violation", title: t("Brudd på opplæring") },
    { value: "information", title: t("Informasjon") },
    { value: "other", title: t("Annet") },
  ];

  // Status options
  const statusOptions = [
    { value: "draft", title: t("Kladd") },
    { value: "submitted", title: t("Innsendt") },
    { value: "under_review", title: t("Under vurdering") },
    { value: "closed", title: t("Lukket") },
  ];

  // User options for responsible person
  const userOptions = useMemo(() => {
    return itemState.data
      ? itemState.data.map((user) => ({
          value: user.id,
          title: user.displayTitle,
        }))
      : [];
  }, [itemState.data]);

  const UploaderCallback = (newDocuments, formik) => {
    const newArray = documents.concat(newDocuments);
    setDocuments(newArray);
    formik.setFieldValue("documents", newArray);
    setIsLoading(false);
  };

  const handleInvolvedEmployeesSelected = (employees) => {
    setInvolvedEmployees(employees);
  };

  const handleSubmit = async (values, helpers) => {
    try {
      setLoading(true);

      // Add involved_employees to the values
      const submitData = {
        ...values,
        involved_employees: involvedEmployees.map((emp) => emp.id),
      };

      let response;

      if (event) {
        response = await updateHseEvent({ ...submitData, id: event.id });
        toast.success(t("Hendelse ble oppdatert"));
      } else {
        response = await createHseEvent(submitData);
        toast.success(t("Hendelse ble opprettet"));
      }

      onSubmit(response.data);
      helpers.setStatus({ success: true });
      helpers.setSubmitting(false);
    } catch (err) {
      console.error("Error saving HSE event:", err);
      toast.error(t("Kunne ikke lagre hendelse"));
      helpers.setStatus({ success: false });
      helpers.setErrors({ submit: err.message });
      helpers.setSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const initialValues = {
    title: event?.title || "",
    description: event?.description || "",
    event_date: event?.event_date || new Date().toISOString().slice(0, 10),
    event_time: formatTimeForInput(event?.event_time),
    event_type: event?.event_type || "",
    severity_level: event?.severity_level || "",
    location: event?.location || "",
    department_id: event?.department_id || "",
    building: event?.building || "",
    room_area: event?.room_area || "",
    location_details: event?.location_details || "",
    involved_persons: event?.involved_persons || "",
    witnesses: event?.witnesses || "",
    responsible_user_id: event?.responsible_user_id
      ? parseInt(event.responsible_user_id)
      : "",
    event_category: event?.event_category || "",
    immediate_cause: event?.immediate_cause || "",
    underlying_cause: event?.underlying_cause || "",
    category_details: event?.category_details || "",
    root_cause_analysis_required: event?.root_cause_analysis_required || false,
    root_cause_analysis_details: event?.root_cause_analysis_details || "",
    immediate_actions_required: event?.immediate_actions_required || false,
    immediate_actions_details: event?.immediate_actions_details || "",
    suggested_actions: event?.suggested_actions || "",
    actions_created: event?.actions_created || false,
    action_notes: event?.action_notes || "",
    status: event?.status || "draft",
    project_id: event?.project_id || "",
    customer_id: event?.customer_id || "",
    folder_id: event?.folder_id || "",
    requires_investigation: event?.requires_investigation || false,
    requires_reporting_to_authorities:
      event?.requires_reporting_to_authorities || false,
    authority_report_details: event?.authority_report_details || "",
    estimated_cost: event?.estimated_cost || "",
    lessons_learned: event?.lessons_learned || "",
    documents: event?.documents || [],
  };

  const validationSchema = Yup.object({
    title: Yup.string().required(t("Tittel er påkrevd")),
    event_date: Yup.date().required(t("Dato er påkrevd")),
    event_type: Yup.string().required(t("Hendelsestype er påkrevd")),
    location: Yup.string().required(t("Sted er påkrevd")),
  });

  return (
    <div className="space-y-6">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form className="space-y-6">
            {isLoading && (
              <ResourceLoading
                size="other"
                position="absolute"
                baseClass=""
                loaderClass="left-1/2 top-1/2"
                text={t("Laster...")}
              />
            )}

            {/* Basic Information Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Grunnleggende informasjon")}
              </h3>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="sm:col-span-2">
                  <PMField
                    name="title"
                    label={t("Tittel")}
                    type="text"
                    placeholder={t("Angi tittel for hendelsen")}
                    {...formik}
                  />
                </div>

                <div>
                  <PMField
                    name="event_date"
                    label={t("Dato")}
                    type="date"
                    {...formik}
                  />
                </div>

                <div>
                  <PMField
                    name="event_time"
                    label={t("Tidspunkt")}
                    type="time"
                    {...formik}
                  />
                </div>

                <div>
                  <PMCombobox
                    name="event_type"
                    label={t("Hendelsestype")}
                    options={eventTypes}
                    placeholder={t("Velg hendelsestype")}
                    {...formik}
                  />
                </div>

                <div>
                  <PMCombobox
                    name="severity_level"
                    label={t("Alvorlighetsgrad")}
                    options={severityLevels}
                    placeholder={t("Velg alvorlighetsgrad")}
                    {...formik}
                  />
                </div>

                <div>
                  <PMCombobox
                    name="status"
                    label={t("Status")}
                    options={statusOptions}
                    {...formik}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Beskrivelse")}
                </label>
                <textarea
                  rows={4}
                  name="description"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder={t("Beskriv hendelsen detaljert...")}
                  value={formik.values.description}
                  onChange={(e) =>
                    formik.setFieldValue("description", e.target.value)
                  }
                />
              </div>
            </div>

            {/* Location Information Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Sted og avdeling")}
              </h3>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <PMField
                    name="location"
                    label={t("Sted")}
                    type="text"
                    {...formik}
                  />
                </div>

                <div>
                  <DepartmentSelector
                    name="department_id"
                    label={t("Avdeling")}
                    placeholder={t("Velg avdeling")}
                    {...formik}
                  />
                </div>

                <div>
                  <PMField
                    name="building"
                    label={t("Bygg/Område")}
                    type="text"
                    {...formik}
                  />
                </div>

                <div>
                  <PMField
                    name="room_area"
                    label={t("Rom/Område")}
                    type="text"
                    {...formik}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Detaljert beskrivelse av stedet")}
                </label>
                <textarea
                  rows={3}
                  name="location_details"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder={t("Beskriv stedet mer detaljert...")}
                  value={formik.values.location_details}
                  onChange={(e) =>
                    formik.setFieldValue("location_details", e.target.value)
                  }
                />
              </div>
            </div>

            {/* People Involved Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Involverte personer")}
              </h3>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <PMCombobox
                    name="responsible_user_id"
                    label={t("Ansvarlig person")}
                    options={userOptions}
                    placeholder={t("Velg ansvarlig person")}
                    {...formik}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("Involverte personer")}
                  </label>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowMultiEmployeeSelector(true)}
                      className="flex-1 text-left px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {involvedEmployees.length > 0
                        ? `${involvedEmployees.length} ${t("personer valgt")}`
                        : t("Velg involverte personer")}
                    </button>
                    {involvedEmployees.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setInvolvedEmployees([])}
                        className="text-red-600 hover:text-red-800"
                      >
                        {t("Fjern alle")}
                      </button>
                    )}
                  </div>
                  {involvedEmployees.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {involvedEmployees.map((employee) => (
                        <span
                          key={employee.id}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {employee.displayTitle}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <PMField
                    name="witnesses"
                    label={t("Vitner")}
                    type="text"
                    {...formik}
                  />
                </div>

                <div>
                  <PMField
                    name="other_persons_involved"
                    label={t("Andre involverte personer")}
                    type="text"
                    placeholder={t("Beskriv andre involverte personer...")}
                    {...formik}
                  />
                </div>
              </div>
            </div>

            {/* Categorization Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Kategorisering")}
              </h3>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <PMCombobox
                    name="event_category"
                    label={t("Kategori")}
                    options={eventCategories}
                    placeholder={t("Velg kategori")}
                    {...formik}
                  />
                </div>

                <div>
                  <PMField
                    name="immediate_cause"
                    label={t("Umiddelbar årsak")}
                    type="text"
                    {...formik}
                  />
                </div>

                <div>
                  <PMField
                    name="underlying_cause"
                    label={t("Underliggende årsak")}
                    type="text"
                    {...formik}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Detaljert kategorisering")}
                </label>
                <textarea
                  rows={3}
                  name="category_details"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder={t("Beskriv kategoriseringen mer detaljert...")}
                  value={formik.values.category_details}
                  onChange={(e) =>
                    formik.setFieldValue("category_details", e.target.value)
                  }
                />
              </div>
            </div>

            {/* Analysis and Actions Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Analyse og tiltak")}
              </h3>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="root_cause_analysis_required"
                    checked={formik.values.root_cause_analysis_required}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "root_cause_analysis_required",
                        e.target.checked
                      )
                    }
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    {t("Årsakanalyse påkrevd")}
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="immediate_actions_required"
                    checked={formik.values.immediate_actions_required}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "immediate_actions_required",
                        e.target.checked
                      )
                    }
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    {t("Strakstiltak påkrevd")}
                  </label>
                </div>
              </div>

              {formik.values.root_cause_analysis_required && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("Årsakanalyse")}
                  </label>
                  <textarea
                    rows={4}
                    name="root_cause_analysis_details"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder={t("Beskriv årsakanalysen...")}
                    value={formik.values.root_cause_analysis_details}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "root_cause_analysis_details",
                        e.target.value
                      )
                    }
                  />
                </div>
              )}

              {formik.values.immediate_actions_required && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("Strakstiltak")}
                  </label>
                  <textarea
                    rows={4}
                    name="immediate_actions_details"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder={t("Beskriv strakstiltakene...")}
                    value={formik.values.immediate_actions_details}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "immediate_actions_details",
                        e.target.value
                      )
                    }
                  />
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Forslag til tiltak")}
                </label>
                <textarea
                  rows={4}
                  name="suggested_actions"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder={t("Beskriv forslag til tiltak...")}
                  value={formik.values.suggested_actions}
                  onChange={(e) =>
                    formik.setFieldValue("suggested_actions", e.target.value)
                  }
                />
              </div>
            </div>

            {/* Related Entities Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Tilknyttede elementer")}
              </h3>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <CustomerTag customer={event?.customer} onRefresh={() => {}} />
                <ProjectTag project={event?.project} onRefresh={() => {}} />
                <EmployeeTag
                  employee={event?.responsible_user}
                  label={t("Ansvarlig")}
                  onRefresh={() => {}}
                />
                <ProjectFolderTag
                  folder={event?.folder}
                  label={t("Mappe")}
                  onRefresh={() => {}}
                />
              </div>
            </div>

            {/* Documents Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Dokumenter")}
              </h3>

              <DocumentUploadSectionV5
                module="hse_event"
                module_param={event?.id}
                parentCallback={(data) => UploaderCallback(data, formik)}
              />
              <TinyDocumentSection
                module="hse_event"
                module_param={event?.id}
                parentCallback={(data) => UploaderCallback(data, formik)}
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {t("Avbryt")}
              </button>
              <button
                type="submit"
                disabled={formik.isSubmitting || loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formik.isSubmitting || loading
                  ? t("Lagrer...")
                  : event
                  ? t("Oppdater")
                  : t("Opprett")}
              </button>
            </div>
          </Form>
        )}
      </Formik>

      {/* MultiEmployeeSelector Dialog */}
      <MultiEmployeeSelector
        open={showMultiEmployeeSelector}
        onClose={() => setShowMultiEmployeeSelector(false)}
        selectedEmployees={involvedEmployees}
        onEmployeesSelected={handleInvolvedEmployeesSelected}
        label={t("Velg involverte personer")}
        placeholder={t("Søk i personer...")}
      />
    </div>
  );
};
